// React dependencies
import React, { forwardRef } from 'react';

// External dependencies
import { makeStyles, TextareaAutosize, TextField } from '@material-ui/core';
import PropTypes from 'prop-types';

// Internal dependencies
import Colours from '../../styles/colours';
import { IsEmpty } from '../../common/checks';
import MText from './MText';

/*************** */
/**** Styling ****/
export const TextFieldStyle = {
  border: {
    onFocusColor: Colours.Black,
    radius:       8,
  },
  text: {
    size: 12.5,
  },
  clearBtn: {
    colour: Colours.LightGray2,
  },
};

const MTextFieldStyle = makeStyles({
  focusColor: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: TextFieldStyle.border.onFocusColor,
      },
    },

    '& textarea': {
      fontSize:   TextFieldStyle.text.size,
      lineHeight: '1rem',
      fontFamily: 'inherit',
      color:      Colours.Gray,
    },
  },

  noBorder: {
    border: 'none',
  },
});

/****** */

const MTextField = forwardRef((props, ref) => {
  const textStyle = MTextFieldStyle();

  const onTextChange = (e) => {
    const newText = String(e.target.value);

    if (!IsEmpty(props.maxCharacters) && props.maxCharacters < newText.length) {
      return;
    }
    props.onChange(newText);
  };

  const onBlur = () => {
    props.onBlur();
  };

  const onSelectChange = (e) => {
    props.onChange(String(e.target.value));
  };

  return (
    <>
      <TextField
        inputRef={ref}
        className={`${textStyle.focusColor} ${props.className}`}
        variant="outlined"
        select={props.select}
        placeholder={props.select ? undefined : (props.placeholder || '')}
        value={props.value}
        size="small"
        fullWidth={props.fullWidth}
        disabled={props.disabled}
        multiline={props.select ? false : props.multiLine}
        SelectProps={props.select ? {
          classes: {
            select: props.inputClass || '',
          },
          ...props.selectProps,
        } : undefined}
        inputProps={props.select ? undefined : {
          // Note:
          // 'enterKeyHit' is needed by Android to display the 'Enter' key in the keyboard.
          // It doesn't always show the enter key when there are multiple text fields in the
          // UI, so having enterKeyHint='enter' ensures
          enterKeyHint: props.multiLine ? 'enter' : `${props.enterKeyHint || 'next'}`,
          style:        {
            fontSize:   TextFieldStyle.text.size,
            lineHeight: '1rem',
            color:      props.textColour || Colours.Gray,
            ...props.multiLine ? { padding: '0.5rem 0.5rem' } : {},
            caretColor: Colours.Black,
          },
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        InputProps={{
          readOnly: props.readOnly,
          classes:  {
            input:          !props.select ? (props.inputClass || '') : '',
            notchedOutline: props.hideBorder ? textStyle.noBorder : '',
          },
          inputComponent: (props.multiLine && !props.select)
            ? TextareaAutosize
            : undefined,

          inputProps: (props.multiLine && !props.select)
            ? {
              rowsMin: props.minRows,
              rowsMax: props.maxRows,
              style:   {
                fontSize:   TextFieldStyle.text.size,
                lineHeight: '1rem',
                fontFamily: 'inherit',
                color:      props.textColour || Colours.Gray,
                padding:    '0.5rem 0.5rem',
                caretColor: Colours.Black,
                border:     'none',
                outline:    'none',
                width:      '100%',
                resize:     'none',
              },
            }
            : {},

          startAdornment: (
            !IsEmpty(props.startAdornment) ? <props.startAdornment /> : null
          ),
          // Note: 'style' below controls the styling of the container; not the input text
          style: {
            borderRadius: TextFieldStyle.border.radius,
            padding:      props.select ? '0' : '0.1rem 0.4rem 0.1rem 0.1rem',
          },
        }}
        onBlur={onBlur}
        onChange={props.select ? onSelectChange : onTextChange}
        onClick={props.onClick}
        onKeyDown={props.onKeyDown}
        style={{ ...props.style }}
      >
        {props.select ? props.children : undefined}
      </TextField>
      {
        props.showCharactersCount && !IsEmpty(props.maxCharacters) && (
          <div className="w-100 d-flex justify-content-end">
            <MText
              colour={Colours.LightGray3}
              text={`(${(props.value && props.value.length) || 0}/${props.maxCharacters})`}
            />
          </div>
        )
      }
    </>
  );
});

MTextField.propTypes = {
  className:           PropTypes.string,
  disabled:            PropTypes.bool,
  enterKeyHint:        PropTypes.oneOf(['enter', 'done', 'go', 'next', 'previous', 'search', 'send']),
  fullWidth:           PropTypes.bool,
  hideBorder:          PropTypes.bool,
  inputClass:          PropTypes.string,
  minRows:             PropTypes.number,
  maxRows:             PropTypes.number,
  multiLine:           PropTypes.bool,
  onBlur:              PropTypes.func,
  onChange:            PropTypes.func,
  onClick:             PropTypes.func,
  onKeyDown:           PropTypes.func,
  placeholder:         PropTypes.string,
  readOnly:            PropTypes.bool,
  select:              PropTypes.bool,
  selectProps:         PropTypes.shape({}),
  children:            PropTypes.node,
  textColour:          PropTypes.string,
  startAdornment:      PropTypes.func,
  style:               PropTypes.shape({}),
  value:               PropTypes.string,
  maxCharacters:       PropTypes.number,
  showCharactersCount: PropTypes.bool,
};

MTextField.defaultProps = {
  className:           '',
  disabled:            false,
  fullWidth:           false,
  hideBorder:          false,
  minRows:             2,
  maxRows:             5,
  multiLine:           true,
  placeholder:         '',
  onBlur:              () => {},
  onClick:             () => {},
  onChange:            () => {},
  onKeyDown:           () => {},
  readOnly:            false,
  select:              false,
  selectProps:         {},
  children:            null,
  textColour:          null,
  startAdornment:      null,
  style:               {},
  value:               '',
  enterKeyHint:        undefined,
  inputClass:          '',
  maxCharacters:       undefined,
  showCharactersCount: false,
};

MTextField.displayName = 'MTextField';

export default MTextField;
